// Workspace members: roles, removal, invitations. The server re-checks every
// action; the UI only hides controls that would be refused.
import { INVITABLE_ROLES, WORKSPACE_ROLES, type BillingInfo, type MeResponse, type MemberInfo, type MembersResponse } from '../../shared/api.ts';
import { formatPrice, TEAM_PRICE, TRIAL_DAYS } from '../../shared/billing.ts';
import { ApiError, del, get, patch, post } from '../api.ts';
import { h } from '../util/dom.ts';
import { busy, field, formError, getMe, header, longDate, mount, relativeTime, rememberWorkspace, ROLE_LABELS, select, setTitle } from './page.ts';

export async function membersPage(slug: string): Promise<void> {
  const me = await getMe();
  const ws = me.workspaces.find((w) => w.slug === slug);
  if (!ws) throw new ApiError(404, 'Workspace not found.');
  rememberWorkspace(ws.slug);
  setTitle(`Members · ${ws.name}`);

  const error = formError();
  const body = h('div', { class: 'stack-lg' });
  mount(header(me, ws, 'members'), h('main', { class: 'page narrow' }, [
    h('div', { class: 'page-head' }, [h('h1', {}, ['Members'])]), error, body,
  ]));

  const act = (work: () => Promise<unknown>) => {
    error.clear();
    return work().then(render, (err: unknown) => error.show(err));
  };

  async function render(): Promise<void> {
    const [data, billing] = await Promise.all([
      get<MembersResponse>(`/api/workspaces/${ws!.id}/members`),
      get<BillingInfo>(`/api/workspaces/${ws!.id}/billing`),
    ]);
    body.replaceChildren(
      ...(data.canManage ? [inviteForm(billing)] : []),
      memberTable(data),
      ...(data.invites.length ? [inviteTable(data)] : []),
    );
  }

  function memberTable(data: MembersResponse): HTMLElement {
    const owners = data.members.filter((m) => m.role === 'owner').length;
    const rows = data.members.map((m) => {
      const self = m.user.id === me.user.id;
      const canTouch = data.canManage && !self && (m.role !== 'owner' || ws!.role === 'owner');
      let roleCell: Node = document.createTextNode(ROLE_LABELS[m.role]!);
      if (canTouch) {
        const roles = WORKSPACE_ROLES.filter((r) => r !== 'owner' || ws!.role === 'owner');
        const s = select(roles.map((r) => [r, ROLE_LABELS[r]!]), m.role, { 'aria-label': `Role for ${m.user.name}` });
        s.addEventListener('change', () => void act(() => patch(`/api/workspaces/${ws!.id}/members/${m.user.id}`, { role: s.value })));
        roleCell = s;
      }
      const actions: Node[] = [];
      if (canTouch) actions.push(removeButton(m, 'Remove', `Remove ${m.user.name} from ${ws!.name}?`));
      if (self && !(m.role === 'owner' && owners === 1)) actions.push(removeButton(m, 'Leave', `Leave ${ws!.name}? You'll lose access to its boards.`));
      return h('tr', {}, [
        h('td', {}, [h('div', { class: 'person' }, [
          h('span', { class: 'avatar', 'aria-hidden': 'true' }, [m.user.name.slice(0, 1).toUpperCase()]),
          h('div', {}, [h('strong', {}, [m.user.name + (self ? ' (you)' : '')]), h('div', { class: 'muted' }, [m.user.email])]),
        ])]),
        h('td', {}, [roleCell]),
        h('td', { class: 'muted' }, [`Joined ${relativeTime(m.joinedAt)}`]),
        h('td', { class: 'actions' }, actions),
      ]);
    });
    return h('section', {}, [
      h('h2', {}, [`${data.members.length} ${data.members.length === 1 ? 'member' : 'members'}`]),
      h('table', { class: 'table' }, [h('tbody', {}, rows)]),
    ]);
  }

  function removeButton(m: MemberInfo, label: string, question: string): HTMLButtonElement {
    const b = h('button', { type: 'button', class: 'btn small subtle' }, [label]);
    b.addEventListener('click', () => {
      if (!confirm(question)) return;
      void busy(b, () => act(async () => {
        await del(`/api/workspaces/${ws!.id}/members/${m.user.id}`);
        if (m.user.id === me.user.id) location.assign('/');
      }));
    });
    return b;
  }

  function inviteForm(billing: BillingInfo): HTMLElement {
    if (billing.mode === 'read-only') {
      return h('section', {}, [h('h2', {}, ['Invite people']),
        h('p', { class: 'muted' }, ['Invitations are paused while the workspace is read-only. ', h('a', { href: `/w/${ws!.slug}/plan` }, ['See the plan']), '.'])]);
    }
    const email = h('input', { type: 'email', required: '', placeholder: 'colleague@company.com', autocomplete: 'off' });
    // Without Team (and with the trial used), members can't be added: default to a guest.
    const membersNeedTeam = billing.mode === 'free' && !billing.trialAvailable;
    const role = select(INVITABLE_ROLES.map((r) => [r, ROLE_LABELS[r]!]), membersNeedTeam ? 'guest' : 'member');
    const send = h('button', { type: 'submit', class: 'btn primary' }, ['Send invite']);
    const form = h('form', { class: 'invite-form' }, [field('Email', email), field('Role', role), send]);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void busy(send, () => act(() => post(`/api/workspaces/${ws!.id}/invites`, { email: email.value, role: role.value })));
    });
    const planLink = h('a', { href: `/w/${ws!.slug}/plan` }, ['See plans']);
    const note = billing.mode === 'free'
      ? billing.trialAvailable
        ? [`Inviting a member starts a free ${TRIAL_DAYS}-day Team trial, so they can edit with you. Guests can view boards you share with them.`]
        : ['Members need the Team plan. ', planLink, '. Guests can view boards you share with them.']
      : billing.status === 'trialing' && billing.trialEndsAt
        ? [`Team trial: invite as many members as you like until ${longDate(billing.trialEndsAt)}. Guests only see boards shared with them.`]
        : [`Each member adds a seat: ${formatPrice(TEAM_PRICE.month)}/month (or ${formatPrice(TEAM_PRICE.year)}/year). Guests are free and only see boards shared with them.`];
    return h('section', {}, [
      h('h2', {}, ['Invite people']),
      h('p', { class: 'invite-note muted' }, note),
      form,
    ]);
  }

  function inviteTable(data: MembersResponse): HTMLElement {
    const rows = data.invites.map((i) => {
      const revoke = h('button', { type: 'button', class: 'btn small subtle' }, ['Revoke']);
      revoke.addEventListener('click', () => void busy(revoke, () => act(() => del(`/api/workspaces/${ws!.id}/invites/${i.id}`))));
      return h('tr', {}, [
        h('td', {}, [i.email]),
        h('td', {}, [ROLE_LABELS[i.role]!]),
        h('td', { class: 'muted' }, [`Invited by ${i.invitedBy}`]),
        h('td', { class: 'actions' }, [revoke]),
      ]);
    });
    return h('section', {}, [h('h2', {}, ['Pending invitations']), h('table', { class: 'table' }, [h('tbody', {}, rows)])]);
  }

  await render();
}
